import { useEffect, useRef, useState } from "react";
import { Share2, Download, X } from "lucide-react";
import sfx from "../utils/sound";
import { toast } from "sonner";

// Renders a shareable "Brag Card" image on a hidden <canvas>.
// Usage: <BragCard open onClose title="Hole in One!" subtitle="On 67Cafe Golf" emoji="⛳" coins={200} name="Alex" />
const BragCard = ({
  open,
  onClose,
  title = "Big Win!",
  subtitle = "Playing on 67Cafe",
  emoji = "🏆",
  coins = 0,
  name = "67Cafe Kid",
}) => {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReady(false);
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const W = c.width; // 720
    const H = c.height; // 720

    // Background gradient
    const bg = ctx.createLinearGradient(0, 0, W, H);
    bg.addColorStop(0, "#FF5A5F");
    bg.addColorStop(0.5, "#F368E0");
    bg.addColorStop(1, "#5F27CD");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // Confetti dots
    const colors = ["#FFB400", "#54D62C", "#54A0FF", "#FFFFFF", "#FFE561"];
    for (let i = 0; i < 60; i++) {
      ctx.beginPath();
      ctx.fillStyle = colors[i % colors.length];
      const cx = Math.random() * W;
      const cy = Math.random() * H;
      const r = 4 + Math.random() * 8;
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
    }

    // Inner card
    const px = 60;
    const py = 90;
    const cardW = W - px * 2;
    const cardH = H - py * 2;
    ctx.fillStyle = "#FFF9E6";
    ctx.strokeStyle = "#2b2b2b";
    ctx.lineWidth = 10;
    // rounded rect (manual for widest browser support)
    const r = 36;
    ctx.beginPath();
    ctx.moveTo(px + r, py);
    ctx.lineTo(px + cardW - r, py);
    ctx.quadraticCurveTo(px + cardW, py, px + cardW, py + r);
    ctx.lineTo(px + cardW, py + cardH - r);
    ctx.quadraticCurveTo(px + cardW, py + cardH, px + cardW - r, py + cardH);
    ctx.lineTo(px + r, py + cardH);
    ctx.quadraticCurveTo(px, py + cardH, px, py + cardH - r);
    ctx.lineTo(px, py + r);
    ctx.quadraticCurveTo(px, py, px + r, py);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // 67Cafe top ribbon
    ctx.fillStyle = "#FF5A5F";
    ctx.beginPath();
    ctx.moveTo(px + 40, py - 10);
    ctx.lineTo(px + cardW - 40, py - 10);
    ctx.lineTo(px + cardW - 40, py + 42);
    ctx.lineTo(px + cardW - 60, py + 62);
    ctx.lineTo(px + 60, py + 62);
    ctx.lineTo(px + 40, py + 42);
    ctx.closePath();
    ctx.fill();
    ctx.lineWidth = 6;
    ctx.stroke();
    ctx.fillStyle = "#fff";
    ctx.font = "bold 34px 'Fredoka','Baloo 2','Comic Sans MS',sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("🎪 67Cafe", W / 2, py + 26);

    // Big emoji
    ctx.font = "180px 'Segoe UI Emoji','Apple Color Emoji',sans-serif";
    ctx.fillText(emoji, W / 2, py + 220);

    // Title
    ctx.fillStyle = "#2b2b2b";
    ctx.font = "bold 56px 'Fredoka','Baloo 2','Comic Sans MS',sans-serif";
    ctx.fillText(title, W / 2, py + 340);

    // Subtitle
    ctx.fillStyle = "#484848";
    ctx.font = "30px 'Fredoka','Baloo 2','Comic Sans MS',sans-serif";
    wrapText(ctx, subtitle, W / 2, py + 390, cardW - 60, 36);

    // Coin badge (centered above the name pill)
    if (coins > 0) {
      const badgeY = py + 440;
      ctx.textBaseline = "middle";
      ctx.font = "bold 56px 'Fredoka','Baloo 2','Comic Sans MS',sans-serif";
      const coinText = `+${coins} coins`;
      const textW = ctx.measureText(coinText).width;
      const coinR = 30;
      const gap = 18;
      const groupW = coinR * 2 + gap + textW;
      const groupStart = W / 2 - groupW / 2;
      // coin circle
      ctx.beginPath();
      ctx.fillStyle = "#FFD700";
      ctx.strokeStyle = "#2b2b2b";
      ctx.lineWidth = 6;
      ctx.arc(groupStart + coinR, badgeY, coinR, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = "#7a5a00";
      ctx.font = "bold 36px 'Fredoka','Baloo 2',sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("$", groupStart + coinR, badgeY + 2);
      // coin count
      ctx.fillStyle = "#2b2b2b";
      ctx.font = "bold 56px 'Fredoka','Baloo 2','Comic Sans MS',sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(coinText, groupStart + coinR * 2 + gap, badgeY + 4);
      ctx.textAlign = "center";
    }

    // Name pill (near the bottom of the card, well below the coin badge)
    ctx.fillStyle = "#F368E0";
    ctx.strokeStyle = "#2b2b2b";
    ctx.lineWidth = 5;
    const nameText = `— ${name} —`;
    ctx.font = "bold 26px 'Fredoka','Baloo 2',sans-serif";
    const tw = ctx.measureText(nameText).width;
    const pillW = Math.min(cardW - 80, tw + 44);
    const pillH = 42;
    const plx = W / 2 - pillW / 2;
    const ply = py + cardH - 40;
    ctx.beginPath();
    ctx.arc(plx + pillH / 2, ply + pillH / 2, pillH / 2, Math.PI / 2, (Math.PI * 3) / 2);
    ctx.lineTo(plx + pillW - pillH / 2, ply);
    ctx.arc(
      plx + pillW - pillH / 2,
      ply + pillH / 2,
      pillH / 2,
      -Math.PI / 2,
      Math.PI / 2
    );
    ctx.lineTo(plx + pillH / 2, ply + pillH);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = "#fff";
    ctx.textBaseline = "middle";
    ctx.fillText(nameText, W / 2, ply + pillH / 2);

    setReady(true);
  }, [open, title, subtitle, emoji, coins, name]);

  if (!open) return null;

  const getBlob = () =>
    new Promise((resolve) => {
      canvasRef.current.toBlob(resolve, "image/png", 0.95);
    });

  const share = async () => {
    try {
      sfx.click();
      const blob = await getBlob();
      const file = new File([blob], "funcart-brag.png", { type: "image/png" });
      if (
        navigator.canShare &&
        navigator.canShare({ files: [file] }) &&
        navigator.share
      ) {
        await navigator.share({
          title: "Look what I did on 67Cafe!",
          text: `${title} — ${subtitle}`,
          files: [file],
        });
      } else {
        // Fallback: download the image.
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "funcart-brag.png";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast("Card downloaded — share it wherever! 📸", { duration: 2000 });
      }
    } catch (err) {
      if (err && err.name !== "AbortError") {
        toast("Couldn't share. Try downloading instead.", { duration: 1800 });
      }
    }
  };

  const download = async () => {
    sfx.click();
    const blob = await getBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "funcart-brag.png";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast("Saved to your device 🎉", { duration: 1600 });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6"
      style={{ background: "rgba(0,0,0,0.6)" }}
      data-testid="brag-card-modal"
      onClick={onClose}
    >
      <div
        className="chunky-card p-5 sm:p-6 max-w-md w-full pop-in"
        style={{ background: "#fff" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3
            className="font-display text-xl sm:text-2xl"
            style={{ color: "#FF5A5F" }}
          >
            Brag Card 📸
          </h3>
          <button
            onClick={onClose}
            className="rounded-full flex items-center justify-center"
            data-testid="brag-close-btn"
            aria-label="Close"
            style={{
              width: 40,
              height: 40,
              background: "#fff",
              border: "3px solid #2b2b2b",
              boxShadow: "0 3px 0 #2b2b2b",
            }}
          >
            <X size={20} strokeWidth={3} />
          </button>
        </div>

        <div
          className="rounded-2xl overflow-hidden mb-4"
          style={{
            border: "3px solid #2b2b2b",
            boxShadow: "0 4px 0 #2b2b2b",
          }}
        >
          <canvas
            ref={canvasRef}
            width={720}
            height={720}
            style={{ display: "block", width: "100%", height: "auto" }}
            data-testid="brag-canvas"
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={share}
            disabled={!ready}
            className="btn-3d btn-pink flex-1"
            data-testid="brag-share-btn"
          >
            <Share2 size={20} strokeWidth={3} />
            Share
          </button>
          <button
            onClick={download}
            disabled={!ready}
            className="btn-3d flex-1"
            data-testid="brag-download-btn"
          >
            <Download size={20} strokeWidth={3} />
            Download
          </button>
        </div>
      </div>
    </div>
  );
};

// Multi-line text helper for canvas.
function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = String(text).split(" ");
  let line = "";
  let lines = [];
  for (let i = 0; i < words.length; i++) {
    const test = line ? line + " " + words[i] : words[i];
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = words[i];
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  lines.slice(0, 3).forEach((ln, i) => {
    ctx.fillText(ln, x, y + i * lineHeight);
  });
}

export default BragCard;
